import { TouchableOpacity, View, Text } from 'react-native'
import { useLoggedInQuery } from '@/src/common/gql/MesaGql'
import { mesaColorNames, typography } from '@/src/common/theme'
import { DesignSystem } from '@/src/design-system'
import { ChevronRight } from '@/src/assets/icons/ChevronRight'
import { useLogError } from '@/src/common/hooks/useLogError'
import {
  GET_CARD_APPLICATION_SUBMISSION_SCREEN,
  GET_CARD_APPLICATION_STATUS,
} from '../../gql/queries'
import { ApplicationSubmissionScreen } from '@api/mesa/graphql'
import { router } from 'expo-router'
import * as E2E from '../../e2e'
import { routeForApplicationSubmissionScreen } from '../../screens/_highnote/applicationProcessing'
import { navigateToApplicationFlow } from '@/src/app/main/(tabs)/card/cardBenefits'

export const ApplicationStatusBanner = () => {
  const onError = useLogError()

  const { data: cardApplicationSubmissionScreen, loading: loadingCardApplicationSubmissionScreen } =
    useLoggedInQuery(GET_CARD_APPLICATION_SUBMISSION_SCREEN, {
      fetchPolicy: 'network-only',
      onError,
    })

  const { data: cardApplicationStatus, loading: loadingCardApplicationStatus } = useLoggedInQuery(
    GET_CARD_APPLICATION_STATUS,
    {
      fetchPolicy: 'network-only',
      onError,
    },
  )

  const getCardApplicationSubmissionScreen =
    cardApplicationSubmissionScreen?.getApplicationSubmissionScreen

  const generateCardApplicationBannerContent = (
    applicationSubmissionScreen: ApplicationSubmissionScreen,
  ) => {
    switch (applicationSubmissionScreen) {
      case ApplicationSubmissionScreen.DocumentUpload:
        return {
          text: `Additional documentation verification is required to process your application.`,
          navigateTo: () => {
            if (cardApplicationStatus?.getCardProductApplicationStatus.status === 'IN_REVIEW') {
              navigateToApplicationFlow({
                cardApplicationStatus: {
                  legacy: cardApplicationStatus.getCardProductApplicationStatus.status,
                },
                navigateTo: (route) => router.push(route),
              })
            } else if (
              cardApplicationStatus?.getCardProductApplicationStatus.status === 'PENDING'
            ) {
              routeForApplicationSubmissionScreen({
                applicationSubmissionScreen,
                navigateTo: (route) => router.push(route),
                handleError: () =>
                  onError(
                    new Error(
                      `Unknown application submission screen: ${applicationSubmissionScreen}`,
                    ),
                  ),
              })
            }
          },
        }
      case ApplicationSubmissionScreen.ManualReviewGeneric:
        return {
          text: `Your application is currently under review. Expect an update within 1-2 business days.`,
        }
      case ApplicationSubmissionScreen.FrozenCredit:
        return {
          text: `Your credit is frozen. Please unfreeze directly with the bureau then return here to proceed.`,
          navigateTo: () =>
            routeForApplicationSubmissionScreen({
              applicationSubmissionScreen,
              navigateTo: (route) => router.push(route),
              handleError: () =>
                onError(
                  new Error(
                    `Unknown application submission screen: ${applicationSubmissionScreen}`,
                  ),
                ),
            }),
        }
      default:
        return {}
    }
  }

  const isLoading = loadingCardApplicationSubmissionScreen || loadingCardApplicationStatus

  if (isLoading || !getCardApplicationSubmissionScreen || !cardApplicationStatus) {
    return <></>
  }

  const cardApplicationBannerContent = generateCardApplicationBannerContent(
    getCardApplicationSubmissionScreen,
  )

  if (!cardApplicationBannerContent.text) {
    return <></>
  }

  return (
    <TouchableOpacity
      style={{ width: '100%' }}
      onPress={() => {
        if (cardApplicationBannerContent.navigateTo) {
          cardApplicationBannerContent.navigateTo()
        }
      }}
      testID={E2E.Config.Ids.Press.CardApplicationStatusBanner}
      disabled={cardApplicationBannerContent.navigateTo === undefined}
    >
      <DesignSystem.Card
        style={{
          borderColor: mesaColorNames['Mesa Primary Scale/400 - Brand #00DFC9'],
          borderWidth: 1,
          backgroundColor: 'white',
          padding: 16,
        }}
      >
        <View
          style={{
            flexDirection: 'row',
          }}
        >
          <View style={{ flex: 1 }}>
            <View>
              <Text
                style={[
                  typography['Base Bold'],
                  { color: mesaColorNames['Mesa - Brand/Blackish #222222'] },
                ]}
                numberOfLines={1}
              >
                Mesa Card Application Status
              </Text>
            </View>

            <Text
              style={[typography.S, { color: mesaColorNames['Mesa - Brand/Blackish #222222'] }]}
            >
              {cardApplicationBannerContent.text}
            </Text>
          </View>

          {cardApplicationBannerContent.navigateTo && (
            <View
              style={{
                justifyContent: 'space-evenly',
                marginLeft: 8,
              }}
            >
              <ChevronRight color={mesaColorNames['Mesa - Brand/Blackish #222222']} size={20} />
            </View>
          )}
        </View>
      </DesignSystem.Card>
    </TouchableOpacity>
  )
}
